import { graphql } from '@referee/graphql-client/client';

export const GET_ADMIN_DASHBOARD_STATS = graphql(/* GraphQL */ `
  query GetAdminDashboardStats {
    getAdminDashboardStats {
      organizationCount
      userCount
    }
  }
`);

export const LIST_ALL_ORGANIZATIONS = graphql(/* GraphQL */ `
  query ListAllOrganizations {
    listAllOrganizations {
      id
      name
      logo
      primaryColor
      welcomeText
      cutoverDate
      locale
      billingInfo {
        organizationId
        startDate
        endDate
        status
        type
        baseFee
        pricePerTest
        pricePerReferee
        completedTestsCount
        activeRefereesCount
        totalFee
      }
    }
  }
`);

export const GET_ADMIN_ORGANIZATION = graphql(/* GraphQL */ `
  query GetAdminOrganization($params: GetAdminOrganizationParams!) {
    getAdminOrganization(params: $params) {
      id
      name
      logo
      primaryColor
      welcomeText
      cutoverDate
      locale
      contactEmail
      billingPeriods {
        organizationId
        startDate
        endDate
        status
        type
        baseFee
        pricePerTest
        pricePerReferee
      }
    }
  }
`);

export const CREATE_ORGANIZATION = graphql(/* GraphQL */ `
  mutation CreateOrganization($params: CreateOrganizationParams!) {
    createOrganization(params: $params) {
      id
      name
      logo
      primaryColor
      welcomeText
    }
  }
`);

export const UPDATE_ORGANIZATION_ADMIN = graphql(/* GraphQL */ `
  mutation UpdateOrganizationAdmin($params: UpdateOrganizationAdminParams!) {
    updateOrganizationAdmin(params: $params) {
      id
      name
      logo
      primaryColor
      welcomeText
      cutoverDate
      locale
      contactEmail
    }
  }
`);

export const LIST_ALL_USERS = graphql(/* GraphQL */ `
  query ListAllUsers($params: ListAllUsersParams) {
    listAllUsers(params: $params) {
      id
      email
      username
      firstName
      lastName
      enabled
      status
      createdAt
      federations {
        organizationId
        organizationName
        role
        status
      }
    }
  }
`);

export const GET_ADMIN_USER = graphql(/* GraphQL */ `
  query GetAdminUser($params: GetAdminUserParams!) {
    getAdminUser(params: $params) {
      id
      email
      username
      firstName
      lastName
      enabled
      status
      createdAt
      federations {
        organizationId
        organizationName
        role
        status
      }
    }
  }
`);

export const LIST_FEDERATION_USERS = graphql(/* GraphQL */ `
  query ListFederationUsers($params: ListFederationUsersParams!) {
    listFederationUsers(params: $params) {
      id
      email
      username
      firstName
      lastName
      enabled
      status
      federations {
        organizationId
        organizationName
        role
        status
      }
    }
  }
`);

export const CREATE_USER = graphql(/* GraphQL */ `
  mutation CreateUser($params: CreateUserParams!) {
    createUser(params: $params) {
      id
      email
      username
      firstName
      lastName
      enabled
      status
      federations {
        organizationId
        organizationName
        role
        status
      }
    }
  }
`);

export const ENABLE_USER = graphql(/* GraphQL */ `
  mutation EnableUser($params: EnableUserParams!) {
    enableUser(params: $params) {
      id
      enabled
    }
  }
`);

export const DISABLE_USER = graphql(/* GraphQL */ `
  mutation DisableUser($params: DisableUserParams!) {
    disableUser(params: $params) {
      id
      enabled
    }
  }
`);

export const ASSIGN_USER_TO_FEDERATION = graphql(/* GraphQL */ `
  mutation AssignUserToFederation($params: AssignUserToFederationParams!) {
    assignUserToFederation(params: $params) {
      id
      federations {
        organizationId
        organizationName
        role
        status
      }
    }
  }
`);

export const REMOVE_USER_FROM_FEDERATION = graphql(/* GraphQL */ `
  mutation RemoveUserFromFederation($params: RemoveUserFromFederationParams!) {
    removeUserFromFederation(params: $params) {
      id
      federations {
        organizationId
        organizationName
        role
        status
      }
    }
  }
`);

export const DELETE_USER = graphql(/* GraphQL */ `
  mutation DeleteUser($params: DeleteUserParams!) {
    deleteUser(params: $params) {
      success
    }
  }
`);

export const REQUEST_LOGO_UPLOAD = graphql(/* GraphQL */ `
  mutation RequestLogoUpload($params: RequestLogoUploadParams!) {
    requestLogoUpload(params: $params) {
      uploadUrl
      logoPath
    }
  }
`);

// ──────────────────────────────────────
// Catalog Group operations
// ──────────────────────────────────────

export const LIST_CATALOG_GROUPS = graphql(/* GraphQL */ `
  query ListCatalogGroups {
    listCatalogGroups {
      id
      name
      type
      createdAt
      versions {
        id
        groupId
        versionName
        status
        languages
        questionCount
        error
        createdAt
        publishedAt
      }
    }
  }
`);

export const GET_CATALOG_GROUP = graphql(/* GraphQL */ `
  query GetCatalogGroup($params: GetCatalogGroupParams!) {
    getCatalogGroup(params: $params) {
      id
      name
      type
      createdAt
      versions {
        id
        groupId
        versionName
        status
        languages
        questionCount
        error
        createdAt
        publishedAt
      }
    }
  }
`);

export const CREATE_CATALOG_GROUP = graphql(/* GraphQL */ `
  mutation CreateCatalogGroup($params: CreateCatalogGroupParams!) {
    createCatalogGroup(params: $params) {
      id
      name
      type
      createdAt
      versions {
        id
      }
    }
  }
`);

export const CREATE_CATALOG_VERSION = graphql(/* GraphQL */ `
  mutation CreateCatalogVersion($params: CreateCatalogVersionParams!) {
    createCatalogVersion(params: $params) {
      id
      groupId
      versionName
      status
      createdAt
    }
  }
`);

export const REQUEST_DRAFT_UPLOAD = graphql(/* GraphQL */ `
  mutation RequestDraftUpload($params: RequestDraftUploadParams!) {
    requestDraftUpload(params: $params) {
      uploadUrls {
        fileName
        uploadUrl
      }
      groupId
      versionId
    }
  }
`);

export const PUBLISH_CATALOG_VERSION = graphql(/* GraphQL */ `
  mutation PublishCatalogVersion($params: PublishCatalogVersionParams!) {
    publishCatalogVersion(params: $params) {
      id
      groupId
      versionName
      status
      languages
      questionCount
      error
      createdAt
      publishedAt
    }
  }
`);

export const UNPUBLISH_CATALOG_VERSION = graphql(/* GraphQL */ `
  mutation UnpublishCatalogVersion($params: UnpublishCatalogVersionParams!) {
    unpublishCatalogVersion(params: $params) {
      id
      groupId
      versionName
      status
      languages
      questionCount
      error
      createdAt
      publishedAt
    }
  }
`);

export const ARCHIVE_CATALOG_VERSION = graphql(/* GraphQL */ `
  mutation ArchiveCatalogVersion($params: ArchiveCatalogVersionParams!) {
    archiveCatalogVersion(params: $params) {
      id
      groupId
      versionName
      status
      languages
      questionCount
      error
      createdAt
      publishedAt
    }
  }
`);

export const DELETE_CATALOG_VERSION = graphql(/* GraphQL */ `
  mutation DeleteCatalogVersion($params: DeleteCatalogVersionParams!) {
    deleteCatalogVersion(params: $params) {
      success
    }
  }
`);

export const ASSIGN_CATALOG_TO_FEDERATION = graphql(/* GraphQL */ `
  mutation AssignCatalogToFederation($params: AssignCatalogToFederationParams!) {
    assignCatalogToFederation(params: $params) {
      organizationId
      organizationName
      catalogId
      assignedAt
    }
  }
`);

export const UNASSIGN_CATALOG_FROM_FEDERATION = graphql(/* GraphQL */ `
  mutation UnassignCatalogFromFederation($params: UnassignCatalogFromFederationParams!) {
    unassignCatalogFromFederation(params: $params) {
      success
    }
  }
`);

export const LIST_FEDERATION_CATALOG_ASSIGNMENTS = graphql(/* GraphQL */ `
  query ListFederationCatalogAssignments($params: ListFederationCatalogAssignmentsParams!) {
    listFederationCatalogAssignments(params: $params) {
      catalogId
      groupId
      groupName
      versionId
      versionName
      assignedAt
    }
  }
`);

export const LIST_CATALOG_ASSIGNMENTS = graphql(/* GraphQL */ `
  query ListCatalogAssignments($params: ListCatalogAssignmentsParams!) {
    listCatalogAssignments(params: $params) {
      organizationId
      organizationName
      catalogId
      assignedAt
    }
  }
`);

// Note: ListCatalogAssignmentsParams only requires groupId (returns all version assignments)

export const GET_CATALOG_VERSION_PREVIEW = graphql(/* GraphQL */ `
  query GetCatalogVersionPreview($params: GetCatalogVersionPreviewParams!) {
    getCatalogVersionPreview(params: $params) {
      groupId
      versionId
      versionName
      status
      languages
      questions {
        id
        answers
        rule
        translations {
          language
          question
          options {
            key
            value
          }
        }
      }
    }
  }
`);

// ──────────────────────────────────────
// Billing operations
// ──────────────────────────────────────

export const GET_ADMIN_BILLING_INFO = graphql(/* GraphQL */ `
  query GetAdminBillingInfo($params: GetAdminBillingInfoParams!) {
    getAdminBillingInfo(params: $params) {
      organizationId
      startDate
      endDate
      status
      type
      baseFee
      pricePerTest
      pricePerReferee
      completedTestsCount
      activeRefereesCount
      totalFee
    }
  }
`);

export const CREATE_BILLING_PERIOD = graphql(/* GraphQL */ `
  mutation CreateBillingPeriod($params: CreateBillingPeriodParams!) {
    createBillingPeriod(params: $params) {
      organizationId
      startDate
      endDate
      status
      type
      baseFee
      pricePerTest
      pricePerReferee
      createdAt
      updatedAt
    }
  }
`);

export const UPDATE_BILLING_PERIOD = graphql(/* GraphQL */ `
  mutation UpdateBillingPeriod($params: UpdateBillingPeriodParams!) {
    updateBillingPeriod(params: $params) {
      organizationId
      startDate
      endDate
      status
      type
      baseFee
      pricePerTest
      pricePerReferee
      createdAt
      updatedAt
    }
  }
`);

export const DELETE_BILLING_PERIOD = graphql(/* GraphQL */ `
  mutation DeleteBillingPeriod($params: DeleteBillingPeriodParams!) {
    deleteBillingPeriod(params: $params) {
      success
    }
  }
`);

export const COMPLETE_STALE_TESTS = graphql(/* GraphQL */ `
  mutation CompleteStaleTests($params: CompleteStaleTestsParams!) {
    completeStaleTests(params: $params) {
      completedCount
    }
  }
`);

// ─── Phase 56: Admin Sessions & Test Governance ───

export const LIST_ADMIN_SESSIONS = graphql(/* GraphQL */ `
  query ListAdminSessions($params: ListAdminSessionsParams!) {
    listAdminSessions(params: $params) {
      sessions {
        id
        organizationId
        name
        time
        status
        testCount
      }
      cursor
    }
  }
`);

export const GET_ADMIN_SESSION_DETAIL = graphql(/* GraphQL */ `
  query GetAdminSession($params: GetAdminSessionParams!) {
    getAdminSession(params: $params) {
      id
      name
      organizationId
      startDate
      status
      tests {
        id
        testeeName
        testeeEmail
        status
        currentScore
        maxScore
        submittedCount
        completeTime
      }
    }
  }
`);

export const MERGE_ADMIN_SESSIONS = graphql(/* GraphQL */ `
  mutation MergeAdminSessions($params: MergeAdminSessionsParams!) {
    mergeAdminSessions(params: $params) {
      success
      survivingSessionId
      movedTestCount
    }
  }
`);

export const DELETE_ADMIN_TEST = graphql(/* GraphQL */ `
  mutation DeleteAdminTest($params: DeleteAdminTestParams!) {
    deleteAdminTest(params: $params) {
      success
      testId
      answerCount
    }
  }
`);

export const ADD_CATALOG_VERSION_LOCALE = graphql(/* GraphQL */ `
  mutation AddCatalogVersionLocale($params: AddCatalogVersionLocaleParams!) {
    addCatalogVersionLocale(params: $params) {
      id
      groupId
      versionName
      status
      languages
      questionCount
      error
      createdAt
      publishedAt
    }
  }
`);

export const LIST_QUESTION_CATEGORIES = graphql(/* GraphQL */ `
  query ListQuestionCategories($params: ListQuestionCategoriesParams!) {
    listQuestionCategories(params: $params) {
      groupId
      categories {
        id
        groupId
        names {
          locale
          name
        }
        sortOrder
      }
      assignments {
        questionId
        categoryId
      }
    }
  }
`);

export const SAVE_QUESTION_CATEGORY = graphql(/* GraphQL */ `
  mutation SaveQuestionCategory($params: SaveQuestionCategoryParams!) {
    saveQuestionCategory(params: $params) {
      id
      groupId
      names {
        locale
        name
      }
      sortOrder
    }
  }
`);

export const DELETE_QUESTION_CATEGORY = graphql(/* GraphQL */ `
  mutation DeleteQuestionCategory($params: DeleteQuestionCategoryParams!) {
    deleteQuestionCategory(params: $params) {
      success
    }
  }
`);

export const REORDER_QUESTION_CATEGORIES = graphql(/* GraphQL */ `
  mutation ReorderQuestionCategories($params: ReorderQuestionCategoriesParams!) {
    reorderQuestionCategories(params: $params) {
      id
      sortOrder
    }
  }
`);

export const ASSIGN_QUESTIONS_TO_CATEGORY = graphql(/* GraphQL */ `
  mutation AssignQuestionsToCategory($params: AssignQuestionsToCategoryParams!) {
    assignQuestionsToCategory(params: $params) {
      success
    }
  }
`);
